import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { PopoverController, ToastController } from '@ionic/angular';
import { ReactionsService } from '../../../services/reactions.service';
import { CommentsService } from '../../../services/comments.service';
import { AuthService } from '../../../firebase/auth.service';
import { UserService } from '../../../auth/user.service';
import { ShareOptionsComponent } from '../../../activity-feed/post-details/share-options/share-options.component';
import { AngularFirestore } from '@angular/fire/compat/firestore';

// Define interface to handle reactions structure
interface Reaction {
  id: string;
  likes: string[];
  collection?: string;
}

interface User {
  uid: string;
  username?: string;
  // Add other user properties as needed
}

@Component({
  selector: 'app-social-actions',
  templateUrl: './social-actions.component.html',
  styleUrls: ['./social-actions.component.scss'],
})
export class SocialActionsComponent implements OnInit, OnDestroy {
  @Input() itemId: string;
  @Input() itemType: string; // 'events', 'pods', etc.
  @Input() item: any;
  @Input() currentUser: any;
  @Input() userId: string; // The user who created the item
  @Input() eventType?: string; // Add this for checking external events
  @Input() likesCount: number;
  @Input() commentsCount: number;
  @Input() hasLiked: boolean;
  @Input() isUserFollowed: boolean;

  loadingLike = false;

  private destroy$ = new Subject<void>();

  constructor(
    private reactionsService: ReactionsService,
    private commentsService: CommentsService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private popoverCtrl: PopoverController,
    private toastCtrl: ToastController,
    private fireStore: AngularFirestore
  ) {}

  ngOnInit() {
    this.getUser();
    this.getLikesInfo();
    this.getCommentsCount();
    this.checkFollowStatus();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUser() {
    const currentAuthUser = this.authService.getCurrentUser();
    if (currentAuthUser) {
      this.authService
        .getUserByUid(currentAuthUser.uid)
        .pipe(takeUntil(this.destroy$))
        .subscribe(user => {
          this.currentUser = user;
        });
    }
  }

  getLikesInfo() {
    this.reactionsService
      .getReactions()
      .pipe(
        takeUntil(this.destroy$),
        map(
          reactions => reactions.filter(r => r.id === this.itemId) as Reaction[]
        )
      )
      .subscribe(data => {
        if (data && data.length > 0) {
          const likes = data[0]?.likes || [];
          this.likesCount = likes.length;
          this.hasLiked = this.currentUser
            ? likes.includes(this.currentUser.uid)
            : false;
        } else {
          this.likesCount = 0;
          this.hasLiked = false;
        }
        this.loadingLike = false;
      });
  }

  getCommentsCount() {
    this.commentsService
      .getCommentCount(this.itemId, this.itemType)
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.commentsCount = count;
      });
  }

  async toggleLike() {
    if (this.loadingLike) return;
    this.loadingLike = true;

    try {
      await this.reactionsService.putLikeOrDislike(this.itemId, this.itemType);
      this.hasLiked = !this.hasLiked;
      this.likesCount = this.hasLiked
        ? this.likesCount + 1
        : this.likesCount - 1;
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      this.loadingLike = false;
    }
  }

  goToComments() {
    // Get the current URL
    const currentUrl = this.router.url;

    // Check if we're already on the media details page
    if (currentUrl.includes('/media/')) {
      // If we're already on the details page, just update the query params
      this.router.navigate([], {
        queryParams: { tab: 'comments' },
        queryParamsHandling: 'merge',
      });
      return;
    }

    // Otherwise, navigate to item details with focus on comments
    const route = this.getRouteForItemType();
    if (route) {
      this.router.navigate([route, this.itemId], {
        queryParams: { focusComments: true },
      });
    }
  }

  getRouteForItemType(): string {
    // Map itemType to the corresponding route
    switch (this.itemType) {
      case 'events':
        return '/events/details';
      case 'pods':
        return '/merfolk/pods';
      case 'pools':
        return '/pools';
      case 'media':
      case 'posts':
        return '/media';
      case 'secondHand':
        return '/second-hand';
      case 'services':
        return '/services';
      default:
        return '/activity-feed';
    }
  }

  async sharePost(event: any) {
    let postUrl = '';
    let postTitle = '';
    let postText = '';

    // Generate the correct URL and content based on the item type
    switch (this.itemType) {
      case 'events':
        postUrl = `${window.location.origin}/events/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Event';
        postText = this.item?.description || 'Check out this event on MerMapp!';
        break;
      case 'pods':
        postUrl = `${window.location.origin}/pods/${this.itemId}`;
        postTitle = this.item?.name || 'MerMapp Pod';
        postText = this.item?.description || 'Check out this pod on MerMapp!';
        break;
      case 'pools':
        postUrl = `${window.location.origin}/pools/${this.itemId}`;
        postTitle = this.item?.name || 'MerMapp Pool';
        postText = this.item?.description || 'Check out this pool on MerMapp!';
        break;
      case 'planetChallenge':
        postUrl = `${window.location.origin}/planet/challenges/${this.itemId}`;
        postTitle = this.item?.name || 'MerMapp Planet Challenge';
        postText =
          this.item?.description || 'Join this planet challenge on MerMapp!';
        break;
      case 'planetExperience':
        postUrl = `${window.location.origin}/planet/experience/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Planet Experience';
        postText =
          this.item?.description ||
          'Check out this planet experience on MerMapp!';
        break;
      case 'secondHand':
        postUrl = `${window.location.origin}/secondhand/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Secondhand Item';
        postText =
          this.item?.description ||
          'Check out this secondhand item on MerMapp!';
        break;
      case 'jobs':
        postUrl = `${window.location.origin}/jobs/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Job';
        postText = this.item?.description || 'Check out this job on MerMapp!';
        break;
      case 'services':
        postUrl = `${window.location.origin}/services/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Service';
        postText =
          this.item?.description || 'Check out this service on MerMapp!';
        break;
      case 'shop':
        const shopUrl = this.item?.shopUrl;
        postUrl = shopUrl || `${window.location.origin}/shops/${this.itemId}`;
        postTitle = this.item?.title || 'MerMapp Shop';
        postText = 'Check out this shop on MerMapp!';
        break;
      case 'userPhoto':
        postUrl = `${window.location.origin}/media/${this.itemId}`;
        postTitle = `${this.item?.username || 'User'}'s Photo`;
        postText = 'Check out this photo on MerMapp!';
        break;
      default:
        postUrl = `${window.location.origin}/media/${this.itemId}`;
        postTitle = `${this.item?.username || 'User'}'s Post`;
        postText = this.item?.message || 'Check out this post on MerMapp!';
        break;
    }

    const popover = await this.popoverCtrl.create({
      component: ShareOptionsComponent,
      componentProps: {
        postUrl: postUrl,
        postTitle: postTitle,
        postText: postText,
        onOptionSelected: (option: string) => {
          this.handleShareOption(option, postUrl, postTitle, postText);
          popover.dismiss();
        },
      },
      event: event,
      translucent: true,
      backdropDismiss: true,
    });

    await popover.present();
  }

  // Handle selected share option
  async handleShareOption(
    option: string,
    postUrl: string,
    postTitle: string,
    postText: string
  ) {
    switch (option) {
      case 'copy':
        await this.copyToClipboard(postUrl);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            postUrl
          )}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            postUrl
          )}&text=${encodeURIComponent(postText)}`,
          '_blank'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            `${postText} ${postUrl}`
          )}`,
          '_blank'
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(
            postTitle
          )}&body=${encodeURIComponent(`${postText} ${postUrl}`)}`,
          '_blank'
        );
        break;
      case 'native':
        await this.useNativeShare(postUrl, postTitle, postText);
        break;
    }
  }

  // Copy URL to clipboard
  async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      const toast = await this.toastCtrl.create({
        message: 'Link copied to clipboard!',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    } catch (err) {
      console.error('Failed to copy: ', err);
      const toast = await this.toastCtrl.create({
        message: 'Failed to copy link',
        duration: 2000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    }
  }

  // Use native share functionality if available
  async useNativeShare(
    postUrl: string,
    title: string = 'MerMapp Post',
    text: string = 'Check out this post on MerMapp!'
  ) {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: postUrl,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      const toast = await this.toastCtrl.create({
        message: 'Share functionality not available on this device',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    }
  }

  checkFollowStatus() {
    if (
      !this.currentUser ||
      !this.userId ||
      this.userId === this.currentUser.uid
    ) {
      return;
    }

    this.fireStore
      .collection('follows', ref =>
        ref
          .where('followerId', '==', this.currentUser.uid)
          .where('followedId', '==', this.userId)
      )
      .valueChanges()
      .pipe(takeUntil(this.destroy$))
      .subscribe(follows => {
        this.isUserFollowed = follows.length > 0;
      });
  }

  follow() {
    if (!this.currentUser || !this.userId) {
      return;
    }

    this.fireStore
      .collection('follows')
      .add({
        followerId: this.currentUser.uid,
        followedId: this.userId,
        timestamp: new Date(),
      })
      .then(() => {
        this.isUserFollowed = true;
      })
      .catch(error => {
        console.error('Error following user:', error);
      });
  }

  unfollow() {
    if (!this.currentUser || !this.userId) {
      return;
    }

    this.fireStore
      .collection('follows', ref =>
        ref
          .where('followerId', '==', this.currentUser.uid)
          .where('followedId', '==', this.userId)
      )
      .get()
      .toPromise()
      .then(snapshot => {
        if (snapshot && !snapshot.empty) {
          const followDoc = snapshot.docs[0];
          return followDoc.ref.delete();
        }
        return null;
      })
      .then(() => {
        this.isUserFollowed = false;
      })
      .catch(error => {
        console.error('Error unfollowing user:', error);
      });
  }

  // Add method to check if event is external
  isExternalEvent(): boolean {
    if (!this.eventType) return false;
    return (
      this.eventType === 'A different website informs & sells tickets' ||
      this.eventType === "A different website informs & it's free"
    );
  }
}

